import React from 'react';
import { Chip, Button } from '@mui/material';
import { Publish as PublishIcon } from '@mui/icons-material';
import { DataManagementConfig, TableColumn } from '@shared/components/data-management/types';
import { AddSurveyDialog } from '../components';
import { SurveyTableData, SurveyFormData } from '../types';
import { useDraftSurveysForDataManagement } from '../hooks/useDraftSurveysForDataManagement';
import {
  useUpdateSurvey,
  useDeleteSurvey,
  useCloseSurvey,
  usePublishSurvey,
} from '../hooks/useSurveys';
import { notificationService } from '@shared/components/notifications/notificationService';

/**
 * Creates table configuration for DRAFT surveys
 * These are surveys that are still being edited and not yet published
 */
export const createDraftSurveysConfig = (
  subjectId: string,
  tenantId: string,
  domainId: string,
  navigate?: (path: string) => void,
  closeSurveyMutation?: ReturnType<typeof useCloseSurvey>,
  publishSurveyMutation?: ReturnType<typeof usePublishSurvey>
): DataManagementConfig<SurveyTableData, SurveyFormData, SurveyFormData> => {
  const useDraftSurveysQuery = () => useDraftSurveysForDataManagement(subjectId);
  const useUpdateSurveyWrapper = () => useUpdateSurvey(subjectId);
  const useDeleteSurveyWrapper = () => useDeleteSurvey(subjectId);

  // Edit Questions action
  const editQuestionsAction = {
    label: 'Edit Questions',
    onClick: (row: SurveyTableData) => {
      if (navigate) {
        navigate(`/app/surveys/${row.id}/questions`);
      } else {
        window.location.href = `/app/surveys/${row.id}/questions`;
      }
    },
    color: 'primary' as const,
    variant: 'outlined' as const,
  };

  // Close Survey action
  const closeSurveyAction = {
    label: 'Close',
    onClick: async (row: SurveyTableData) => {
      if (!closeSurveyMutation) {
        console.error('Close survey mutation not provided');
        return;
      }
      try {
        await closeSurveyMutation.mutateAsync(row.id);
      } catch (error) {
        console.error('Failed to close survey:', error);
        notificationService.error('Failed to close survey. Please try again.');
      }
    },
    color: 'warning' as const,
    variant: 'outlined' as const,
  };

  return {
    hooks: {
      useQuery: useDraftSurveysQuery,
      useUpdate: useUpdateSurveyWrapper,
      useDelete: useDeleteSurveyWrapper,
    },

    table: {
      title: 'Draft Surveys',
      columns: [
        {
          label: 'Title',
          key: 'title',
          sx: {
            minWidth: '25%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        },
        {
          label: 'Status',
          key: 'status',
          render: () => <Chip label="Draft" color="warning" variant="outlined" size="small" />,
        },
        {
          label: 'Publish',
          key: 'id',
          render: (_value, row: SurveyTableData) => (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<PublishIcon />}
              onClick={async (e) => {
                e.stopPropagation();
                if (!publishSurveyMutation) {
                  console.error('Publish survey mutation not provided');
                  return;
                }
                try {
                  await publishSurveyMutation.mutateAsync(row.id);
                } catch (error) {
                  console.error('Failed to publish survey:', error);
                }
              }}
              sx={{ minWidth: 'auto' }}
            >
              Publish
            </Button>
          ),
        },
        {
          label: 'Created',
          key: 'createdAt',
          render: (value: string) => new Date(value).toLocaleDateString(),
        },
      ] as TableColumn<SurveyTableData>[],
      emptyMessage: '📝 No draft surveys yet!\nCreate your first survey to get started.',
      errorMessage: 'Failed to load draft surveys. Please try again.',
    },

    actions: {
      create: {
        label: 'Add Survey',
        // eslint-disable-next-line react/display-name -- Anonymous component in table config object
        dialog: React.memo<{
          open: boolean;
          onClose: () => void;
          editingItem?: SurveyTableData | null;
          onSuccess?: () => void;
        }>((props) => (
          <AddSurveyDialog
            {...props}
            subjectId={subjectId}
            tenantId={tenantId}
            domainId={domainId}
          />
        )),
      },
      edit: {
        label: 'Edit',
      },
      // delete: {
      //   label: 'Delete',
      //   confirmTitle: 'Delete Survey',
      //   confirmMessage: (item: SurveyTableData) =>
      //     `Are you sure you want to delete the survey "${item.title}"? This action cannot be undone.`,
      // },
      custom: [editQuestionsAction, closeSurveyAction],
    },

    features: {
      search: true,
      pagination: true,
      onRowClick: (row: SurveyTableData) => {
        if (navigate) {
          navigate(`/app/surveys/${row.id}/questions`);
        } else {
          window.location.href = `/app/surveys/${row.id}/questions`;
        }
      },
    },
  };
};
